import React from "react";
import { 
    FiUser, 
    FiEdit3, 
    FiCheckSquare, 
    FiShoppingCart, 
    FiPackage, 
    FiTool, 
    FiClipboard,
    FiExternalLink,
    FiPlay,
    FiCheck,
    FiAward,
    FiPenTool
} from "react-icons/fi";
import "./ProcessFlowChart.css";
import StepCodeTag from "../../../components/shared/StepCodeTag";

const ProcessFlowChart = ({ orderData }) => {
    // YouTube links for each step
    const stepYouTubeLinks = {
        1: "https://www.youtube.com/watch?v=P0gLIieko24",
        2: "https://www.youtube.com/watch?v=D7_uqgonRV0",
        3: "https://www.youtube.com/watch?v=pjY4Glq8zv4",
        4: "https://www.youtube.com/watch?v=2nd2154VlXE",
        5: "https://www.youtube.com/watch?v=0KwIezS_UKY",
        6: "https://www.youtube.com/watch?v=q0Ql3J1P9V8",
        7: "https://www.youtube.com/watch?v=oOCP7TE_4Lo",
        8: "https://www.youtube.com/watch?v=Vze9z2jC9e0",
        9: "https://www.youtube.com/watch?v=AmVEevcD0qA",
        10: "https://www.youtube.com/watch?v=XG2ulEqJoXk",
    };

    // Define all workflow steps (10 steps)
    const steps = [
        {
            id: 1,
            number: "01",
            stepCode: "A2",
            title: "Open Invoice",
            subtitle: "Create Order",
            icon: FiUser,
            role: "Salesman",
            color: "#00BCD4"
        },
        {
            id: 2,
            number: "02",
            stepCode: "B1",
            title: "Draft Upload",
            subtitle: "Create Blueprint",
            icon: FiEdit3,
            role: "Drafter",
            color: "#2196F3"
        },
        {
            id: 3,
            number: "03",
            stepCode: "A3",
            title: "Customer Sign",
            subtitle: "Blueprint Approval",
            icon: FiPenTool,
            role: "Salesman",
            color: "#009688"
        },
        {
            id: 4,
            number: "04",
            stepCode: "A4",
            title: "Collect 50%",
            subtitle: "Deposit Collection",
            icon: FiCheckSquare,
            role: "Salesman",
            color: "#673AB7"
        },
        {
            id: 5,
            number: "05",
            stepCode: "D1",
            title: "Purchasing",
            subtitle: "Materials Ordering",
            icon: FiShoppingCart,
            role: "Purchaser",
            color: "#C2185B"
        },
        {
            id: 6,
            number: "06",
            stepCode: "E1",
            title: "Production",
            subtitle: "Materials Used",
            icon: FiPackage,
            role: "Producer",
            color: "#E91E63"
        },
        {
            id: 7,
            number: "07",
            stepCode: "E2",
            title: "Confirm Production",
            subtitle: "Production Completed",
            icon: FiCheck,
            role: "Producer",
            color: "#F44336"
        },
        {
            id: 8,
            number: "08",
            stepCode: "F1",
            title: "Installer",
            subtitle: "Installation",
            icon: FiTool,
            role: "Installer",
            color: "#FF9800"
        },
        {
            id: 9,
            number: "09",
            stepCode: "A5",
            title: "Customer Sign",
            subtitle: "Survey & Sign Off",
            icon: FiClipboard,
            role: "Salesman",
            color: "#4CAF50"
        },
        {
            id: 10,
            number: "10",
            stepCode: "A6",
            title: "Complete Order",
            subtitle: "Salesman Sign Off",
            icon: FiAward,
            role: "Salesman",
            color: "#2E7D32"
        }
    ];

    // Determine which steps are completed based on each step's own data only
    const getCompletedSteps = () => {
        if (!orderData) return [];
        
        const completed = [];
        
        // Step 1: Open Invoice - Order created
        if (orderData.createdAt) {
            completed.push(1);
        }
        
        // Step 2: Draft Upload - Blueprint uploaded
        if (orderData.uploadedDraftDate || orderData.blueprint?.length > 0) {
            completed.push(2);
        }
        
        // Step 3: Customer Sign Blueprint - Customer approved blueprint
        if (
            orderData.customerApprovedDate ||
            orderData.customerApprovalSignature
        ) {
            completed.push(3);
        }
        
        // Step 4: Collect 50% - Deposit collected (skip50Percent does not count as collected)
        const orderTotal = orderData.amount || 0;
        const deposit = orderData.deposit || 0;
        const fiftyPercentCollected =
            !!orderData.depositDate ||
            (orderTotal > 0 && deposit >= orderTotal * 0.5);
        if (fiftyPercentCollected) {
            completed.push(4);
        }
        
        // Step 5: Purchasing - PO submitted or confirmed
        if (orderData.purchasingDate || orderData.purchaseOrderApprovedDate) {
            completed.push(5);
        }

        // Step 6: Production - Production materials submitted
        if (orderData.productionDate || orderData.productionApprovalDate) {
            completed.push(6);
        }

        // Step 7: Confirm Production - Production fully confirmed
        if (orderData.productionApprovalDate) {
            completed.push(7);
        }
        
        // Step 8: Installer - Journal filled in by installer manager
        const activeJournals = (orderData.journals || []).filter(j => j.status !== "inactive");
        if (activeJournals.length > 0 || orderData.installingDate) {
            completed.push(8);
        }
        
        // Step 9: Customer Sign - Survey & sign off completed
        if (orderData.survey?.surveyDate || orderData.installApprovedDate) {
            completed.push(9);
        }

        // Step 10: Complete Order - Salesman final sign off
        if (orderData.status === "completed") {
            completed.push(10);
        }
        
        return completed;
    };

    const completedSteps = getCompletedSteps();
    
    // All incomplete steps are actionable (parallel workflow - steps don't wait for each other)
    const allStepIds = steps.map(s => s.id);
    const pendingSteps = allStepIds.filter(id => !completedSteps.includes(id));
    let currentStep = pendingSteps.length > 0 ? pendingSteps[0] : null;

    const orderTotalForSkip = orderData?.amount || 0;
    const depositForSkip = orderData?.deposit || 0;
    const fiftyPercentCollectedForSkip =
        !!orderData?.depositDate ||
        (orderTotalForSkip > 0 && depositForSkip >= orderTotalForSkip * 0.5);
    if (
        orderData?.skip50Percent === true &&
        !fiftyPercentCollectedForSkip &&
        currentStep === 4
    ) {
        currentStep =
            allStepIds.find((id) => id > 4 && !completedSteps.includes(id)) ?? 5;
    }

    const isJBBranch =
        orderData?.createdBy?.address?.city === "Johor Bahru" ||
        orderData?.cashier?.address?.city === "Johor Bahru" ||
        orderData?.installer?.name?.toLowerCase()?.includes("jb");

    const isSerembanBranch =
        orderData?.createdBy?.address?.city === "Seremban" ||
        orderData?.cashier?.address?.city === "Seremban" ||
        orderData?.installer?.name?.toLowerCase()?.includes("seremban");

    const isIpohBranch =
        orderData?.createdBy?.address?.city === "Ipoh" ||
        orderData?.cashier?.address?.city === "Ipoh" ||
        orderData?.installer?.name?.toLowerCase()?.includes("ipoh");

    const jbDefaults = {
        2: "Nicky Gan",
        6: "Ser Wei Yong",
        7: "Ser Wei Yong",
        8: "Ser Wei Yong",
    };

    const serembanDefaults = {
        2: "Ira",
        5: "Judy",
        6: "David",
        7: "David",
        8: "David",
    };

    const ipohDefaults = {
        2: "Khor Teng Aik",
    };

    const getBranchDefault = (stepId) => {
        if (isJBBranch && jbDefaults[stepId]) return jbDefaults[stepId];
        if (isSerembanBranch && serembanDefaults[stepId]) return serembanDefaults[stepId];
        if ((isIpohBranch || (!isJBBranch && !isSerembanBranch)) && ipohDefaults[stepId]) return ipohDefaults[stepId];
        return null;
    };

    // Get the person name in charge for each step based on orderData
    const getPersonInCharge = (stepId) => {
        if (!orderData) return null;
        
        let name = null;
        switch (stepId) {
            case 1: // Open Invoice - Salesman
                name = orderData.cashier?.name || null;
                break;
            case 2: // Draft Upload - Drafter
                name = orderData.artist?.name || null;
                break;
            case 3: // Customer Sign Blueprint - Salesman
                name = orderData.cashier?.name || null;
                break;
            case 4: // Collect 50% - Salesman
                name = orderData.cashier?.name || null;
                break;
            case 5: // Purchasing - Purchaser
                name = orderData.purchaser?.name || null;
                break;
            case 6: // Production - Producer
            case 7: // Confirm Production - Producer
                name = orderData.producer?.name || null;
                break;
            case 8: // Installer
                name = orderData.installer?.name || null;
                break;
            case 9: // Customer Sign - Survey & sign off
                name = orderData.cashier?.name || null;
                break;
            case 10: // Complete Order - Salesman
                name = orderData.cashier?.name || null;
                break;
            default:
                return null;
        }

        if (!name) {
            return getBranchDefault(stepId);
        }
        return name;
    };

    const getStepRoleLabels = (stepId) => {
        const step = steps.find((s) => s.id === stepId);
        if (!step?.dualRoles) {
            return [{ role: step?.role, name: getPersonInCharge(stepId) }];
        }

        return step.dualRoles.map(({ role, personKey, stepCode }) => {
            const person = orderData?.[personKey];
            let name = person?.name || null;
            if (!name && role === "Salesman") {
                name = getPersonInCharge(4);
            }
            return { role, name, stepCode };
        });
    };

    // Get all pending steps for the text display (parallel workflow)
    const getCurrentStepInfo = () => {
        if (!currentStep) {
            return { current: null, upcoming: [] };
        }

        const currentStepData = steps.find((s) => s.id === currentStep);
        const upcomingSteps = steps.filter(
            (s) => pendingSteps.includes(s.id) && s.id !== currentStep
        );

        return {
            current: currentStepData,
            upcoming: upcomingSteps,
        };
    };

    const { current, upcoming } = getCurrentStepInfo();

    // Truncate name to 15 characters + "..." if too long
    const truncateName = (name) => {
        if (!name) return null;
        return name.length > 15 ? name.substring(0, 15) + "..." : name;
    };

    return (
        <div className="process-flow-wrapper">
            <div className="process-flow-header">
                <h2 className="process-flow-title">PROCESS INFOGRAPHIC</h2>
                <div className="process-flow-dots">
                    {steps.map((step, index) => (
                        <span 
                            key={index} 
                            className="dot" 
                            style={{ 
                                backgroundColor: completedSteps.includes(step.id) 
                                    ? step.color 
                                    : '#E0E0E0' 
                            }}
                        ></span>
                    ))}
                </div>
            </div>

            <div className="process-flow-container">
                {steps.map((step, index) => (
                    <React.Fragment key={step.id}>
                        <div 
                            className={`step-wrapper ${index % 2 === 0 ? 'step-bottom' : 'step-top'} ${pendingSteps.includes(step.id) ? 'current-wrapper' : ''}`}
                            style={{
                                '--step-color': step.color,
                                '--step-color-light': step.color + 'CC'
                            }}
                        >
                            {step.stepCode && !step.dualRoles && (
                                <div className="process-flow-step-code">
                                    <StepCodeTag code={step.stepCode} size="sm" />
                                </div>
                            )}
                            {step.dualRoles ? (
                                <div className="step-dual-roles">
                                    {getStepRoleLabels(step.id).map(({ role, name, stepCode }) => (
                                        <div key={role} className="step-dual-role-block">
                                            {stepCode && (
                                                <div className="process-flow-step-code">
                                                    <StepCodeTag code={stepCode} size="sm" />
                                                </div>
                                            )}
                                            <div className="step-person-name">{role}</div>
                                            {name && (
                                                <div className="step-assigned-name">
                                                    {truncateName(name)}
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <>
                                    <div className="step-person-name">{step.role}</div>
                                    {getPersonInCharge(step.id) && (
                                        <div className="step-assigned-name">
                                            {truncateName(getPersonInCharge(step.id))}
                                        </div>
                                    )}
                                </>
                            )}
                            <div 
                                className={`process-step ${completedSteps.includes(step.id) ? 'completed' : ''} ${pendingSteps.includes(step.id) ? 'current' : ''}`}
                            >
                                <div className="step-number">{step.number}</div>
                                <div className="step-content">
                                    <div className="step-text">
                                        <div className="step-title">{step.title} {step.subtitle}</div>
                                    </div>
                                </div>
                                <div className="step-icon-wrapper">
                                    <step.icon className="step-icon" />
                                </div>
                            </div>
                        </div>
                        
                        {index < steps.length - 1 && (
                            <div className="step-connector">
                                <div className={`connector-circle ${completedSteps.includes(step.id) && completedSteps.includes(steps[index + 1].id) ? 'completed' : ''}`}></div>
                            </div>
                        )}
                    </React.Fragment>
                ))}
            </div>

            {/* Text Version of Process Steps */}
            <div className="process-text-section">
                {completedSteps.length === steps.length ? (
                    // All steps completed
                    <div className="process-completed-banner">
                        <div className="completed-icon">✓</div>
                        <div className="completed-text">
                            <h3>All Process Steps Completed</h3>
                            <p>This order has been successfully completed through all 10 workflow stages.</p>
                        </div>
                    </div>
                ) : current ? (
                    // Show current and upcoming steps
                    <div className="process-steps-text">
                        {/* Next Step */}
                        <div className="current-step-card" style={{ '--accent-color': current.color }}>
                            <div className="step-card-badges">
                                {current.dualRoles ? (
                                    getStepRoleLabels(current.id).map(({ role, stepCode }) =>
                                        stepCode ? (
                                            <StepCodeTag key={role} code={stepCode} size="sm" />
                                        ) : null
                                    )
                                ) : (
                                    current.stepCode && (
                                        <StepCodeTag code={current.stepCode} size="sm" />
                                    )
                                )}
                                <div className="step-badge current-badge">NEXT STEP</div>
                            </div>
                            <div className="step-card-header">
                                <div className="step-card-number" style={{ backgroundColor: current.color }}>
                                    {current.number}
                                </div>
                                <div className="step-card-info">
                                    <h4 className="step-card-title">{current.title}</h4>
                                    <p className="step-card-subtitle">{current.subtitle}</p>
                                </div>
                            </div>
                            <div className={`step-card-person${current.dualRoles ? " step-card-person-dual" : ""}`}>
                                {current.dualRoles ? (
                                    <>
                                        {getStepRoleLabels(current.id).map(({ role, name, stepCode }) => (
                                            <div key={role} className="step-card-person-row">
                                                {stepCode && <StepCodeTag code={stepCode} size="sm" />}
                                                <span className="person-label">{role}:</span>
                                                <span
                                                    className="person-name"
                                                    style={{ color: current.color }}
                                                >
                                                    {name || role}
                                                </span>
                                            </div>
                                        ))}
                                    </>
                                ) : current.stepCode ? (
                                    <>
                                        <StepCodeTag code={current.stepCode} size="sm" />
                                        <span className="person-label">Person in Charge:</span>
                                        <span className="person-name" style={{ color: current.color }}>
                                            {getPersonInCharge(current.id) || current.role}
                                        </span>
                                        <span className="person-role">({current.role})</span>
                                    </>
                                ) : (
                                    <>
                                        <span className="person-label">Person in Charge:</span>
                                        <span className="person-name" style={{ color: current.color }}>
                                            {getPersonInCharge(current.id) || current.role}
                                        </span>
                                        <span className="person-role">({current.role})</span>
                                    </>
                                )}
                            </div>
                            <div className="step-card-action">
                                <FiCheckSquare className="action-icon" style={{ color: current.color }} />
                                <span>
                                    {current.dualRoles
                                        ? "Salesman shares blueprint link, then customer signs"
                                        : `Awaiting action from ${getPersonInCharge(current.id) || current.role}`}
                                </span>
                            </div>
                            {stepYouTubeLinks[current.id] && (
                                <div className="step-card-youtube-button">
                                    <button
                                        className="youtube-link-button"
                                        onClick={() => window.open(stepYouTubeLinks[current.id], '_blank', 'noopener,noreferrer')}
                                        style={{ 
                                            backgroundColor: current.color,
                                            '--hover-color': current.color
                                        }}
                                    >
                                        <FiPlay className="youtube-icon" />
                                        <span>Watch Tutorial</span>
                                        <FiExternalLink className="external-link-icon" />
                                    </button>
                                </div>
                            )}
                        </div>

                        {/* Other Pending Steps */}
                        {upcoming.length > 0 && (
                            <div className="upcoming-steps">
                                <h5 className="upcoming-title">Other Pending Steps (can proceed independently)</h5>
                                <div className="upcoming-steps-scroll-wrapper">
                                    <div className="upcoming-steps-list">
                                        {upcoming.map((step, idx) => (
                                            <div key={step.id} className="upcoming-step-item">
                                                {step.dualRoles ? (
                                                    getStepRoleLabels(step.id).map(({ role, stepCode }) =>
                                                        stepCode ? (
                                                            <StepCodeTag key={role} code={stepCode} size="sm" />
                                                        ) : null
                                                    )
                                                ) : (
                                                    step.stepCode && (
                                                        <StepCodeTag code={step.stepCode} size="sm" />
                                                    )
                                                )}
                                                <div 
                                                    className="upcoming-step-number" 
                                                    style={{ 
                                                        backgroundColor: step.color + '20',
                                                        color: step.color,
                                                        borderColor: step.color
                                                    }}
                                                >
                                                    {step.number}
                                                </div>
                                                <div className="upcoming-step-info">
                                                    <span className="upcoming-step-title">{step.title} - {step.subtitle}</span>
                                                    {step.dualRoles ? (
                                                        <span className="upcoming-step-person">
                                                            {getStepRoleLabels(step.id)
                                                                .map(({ role, name }) => `${role}: ${name || role}`)
                                                                .join(" · ")}
                                                        </span>
                                                    ) : (
                                                        <span className="upcoming-step-person">
                                                            {getPersonInCharge(step.id) || step.role}
                                                        </span>
                                                    )}
                                                </div>
                                                {idx < upcoming.length - 1 && (
                                                    <div className="upcoming-connector"></div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Completed Steps Summary */}
                        {completedSteps.length > 0 && (
                            <div className="completed-steps-summary">
                                <h5 className="completed-summary-title">
                                    <span className="checkmark">✓</span>
                                    {completedSteps.length} of {steps.length} Steps Completed
                                </h5>
                                <div className="completed-steps-list">
                                    {steps.filter(s => completedSteps.includes(s.id)).map(step => (
                                        <div key={step.id} className="completed-step-chip" style={{ backgroundColor: step.color }}>
                                            {step.dualRoles ? (
                                                getStepRoleLabels(step.id).map(({ role, stepCode }) =>
                                                    stepCode ? (
                                                        <span key={role} className="chip-step-code">
                                                            <StepCodeTag code={stepCode} size="sm" />
                                                        </span>
                                                    ) : null
                                                )
                                            ) : (
                                                step.stepCode && (
                                                    <span className="chip-step-code">
                                                        <StepCodeTag code={step.stepCode} size="sm" />
                                                    </span>
                                                )
                                            )}
                                            <span className="chip-number">{step.number}</span>
                                            <span className="chip-title">{step.title}</span>
                                            <span className="chip-person">
                                                {step.dualRoles
                                                    ? getStepRoleLabels(step.id)
                                                          .map(({ role, name }) => name || role)
                                                          .join(" · ")
                                                    : getPersonInCharge(step.id) || step.role}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                ) : (
                    // No steps started
                    <div className="process-not-started">
                        <p>Process flow has not started yet. Waiting for Step 01 to begin.</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ProcessFlowChart;

